import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'

import { organizationsQueryKeys } from '@/hooks/organization/types'

import type {
  Organization,
  UpdateDefaultOrganization,
} from '@/types/Organization'

const updateOrganization = async (
  req: UpdateDefaultOrganization,
  accessToken: string | undefined | null,
): Promise<Organization> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }
  const res: Response = await fetch(`${API_URL}/api/v1/organizations/default`, {
    method: 'POST',
    body: JSON.stringify(req),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return convertDatesInObject(await res.json())
}

export const useUpdateDefaultOrganization = () => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()

  return useMutation<
    Organization,
    Error,
    [Organization],
    Organization | undefined
  >({
    mutationFn: ([organization]) =>
      updateOrganization(
        { organization_id: organization.id },
        user?.accessToken,
      ),
    onMutate: async ([organization]) => {
      const key = organizationsQueryKeys.default()
      await queryClient.cancelQueries({ queryKey: key })

      // snapshot the previous value
      const previousData = queryClient.getQueryData<Organization>(key)

      // optimistically update
      if (previousData) {
        queryClient.setQueryData<Organization>(key, organization)
      }

      // return rollback value
      return previousData
    },
    onError: (err, [organization], context) => {
      // rollback to the previous value
      const key = organizationsQueryKeys.default()
      queryClient.setQueryData(key, context)
    },
    onSuccess: (data, [organization]) => {
      const key = organizationsQueryKeys.default()
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
